import React, { useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { ModalSize } from '../../utils/constants/StyleConstant';
import { EMButton } from '../button/EMButton';
import { ModalStackPropsBase } from '../modal/ModalStackProvider';
import { Observable, isObservable } from 'rxjs';

export interface IConfirmModalData {
  modalSize?: ModalSize;
  title?: string;
  submitText?: string;
  cancelText?: string;
  unscrollable?: boolean;
}

export interface IConfirmModalElementID {
  title?: string;
  btnPrimary?: string;
  btnSecondary?: string;
}

interface IConfirmModalProps
  extends ModalStackPropsBase<unknown, unknown>,
    React.PropsWithChildren<{}> {
  data: IConfirmModalData;
  elementID?: IConfirmModalElementID;
  isLoading?: boolean;
  onClose: () => void;
  elementRef?: any;
  iconTitle?: any;
  showCancel?: boolean;
  backdropClassName?: string;
}

export function ConfirmModal(
  props: IConfirmModalProps,
): React.FunctionComponentElement<IConfirmModalProps> {
  const {
    data,
    elementID,
    isLoading,
    onClose,
    onSubmit,
    elementRef,
    children,
    show,
    iconTitle,
    showCancel = true,
    backdropClassName = '',
  } = props;
  const [_loading, _setLoading] = useState(isLoading);

  const { t } = useTranslation();

  const handleSubmit = () => {
    if (onSubmit) {
      if (isObservable(onSubmit)) {
        _setLoading(true);
        (onSubmit as Observable<any>).subscribe().add(() => _setLoading(false));
      } else {
        onSubmit(undefined);
      }
    }
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <>
      {
        <Modal
          show={show}
          size={data.modalSize === ModalSize.MD ? undefined : data.modalSize}
          dialogClassName={data.modalSize === ModalSize.MD ? 'modal-md' : ''}
          onHide={handleCancel}
          scrollable={!data.unscrollable}
          backdropClassName={backdropClassName}
        >
          <Modal.Header>
            {iconTitle}
            <Modal.Title id={elementID?.title}>{data.title ? t(data.title) : 'Title'}</Modal.Title>
          </Modal.Header>

          <Modal.Body ref={elementRef}>{children}</Modal.Body>

          <Modal.Footer className="xs-full-button">
            <div className="float-right">
              {showCancel && (
                <Button
                  id={elementID?.btnSecondary}
                  disabled={_loading}
                  variant="secondary"
                  onClick={handleCancel}
                >
                  {data.cancelText ? t(data.cancelText) : t('Common.Button.Cancel')}
                </Button>
              )}
              <EMButton
                id={elementID?.btnPrimary}
                type="primary"
                isLoading={_loading}
                onClick={handleSubmit}
              >
                {data.submitText ? t(data.submitText) : t('Common.Button.Confirm')}
              </EMButton>
            </div>
          </Modal.Footer>
        </Modal>
      }
    </>
  );
}
